import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api/auth'
import type { User, LoginData, RegisterData } from '@/types/models'

export const useAuthStore = defineStore('auth', () => {
  // ===== 狀態 =====
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('token'))
  const refreshTokenValue = ref<string | null>(
    localStorage.getItem('refreshToken')
  )
  const isLoading = ref(false)

  // ===== 計算屬性 =====
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const subscriptionTier = computed(() => user.value?.subscription_tier ?? 'free')
  const renderCredits = computed(() => user.value?.render_credits ?? 0)

  // ===== 動作 =====

  // 登入
  async function login(data: LoginData) {
    isLoading.value = true
    try {
      const response = await authApi.login(data)
      const { access_token, refresh_token, user: userData } = response.data

      token.value = access_token
      refreshTokenValue.value = refresh_token
      user.value = userData

      localStorage.setItem('token', access_token)
      localStorage.setItem('refreshToken', refresh_token)

      return userData
    } finally {
      isLoading.value = false
    }
  }

  // 註冊
  async function register(data: RegisterData) {
    isLoading.value = true
    try {
      await authApi.register(data)
      // 註冊成功後自動登入
      return await login({ email: data.email, password: data.password })
    } finally {
      isLoading.value = false
    }
  }

  // 登出
  async function logout() {
    try {
      // 通知後端使 token 失效
      if (token.value) {
        await authApi.logout()
      }
    } catch {
      // 即使 API 呼叫失敗，仍然清除本地狀態
    } finally {
      token.value = null
      refreshTokenValue.value = null
      user.value = null
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
    }
  }

  // 刷新 Token
  async function refreshToken() {
    if (!refreshTokenValue.value) {
      throw new Error('No refresh token')
    }

    const response = await authApi.refresh(refreshTokenValue.value)
    const { access_token, refresh_token } = response.data

    token.value = access_token
    refreshTokenValue.value = refresh_token

    localStorage.setItem('token', access_token)
    localStorage.setItem('refreshToken', refresh_token)
  }

  // 取得當前使用者
  async function fetchCurrentUser() {
    if (!token.value) return

    try {
      const response = await authApi.me()
      // 後端直接回傳 UserInfo，不是包在 data 裡
      user.value = response.data
    } catch {
      await logout()
    }
  }

  // 初始化（App 載入時呼叫）
  async function init() {
    if (token.value) {
      await fetchCurrentUser()
    }
  }

  return {
    // 狀態
    user,
    token,
    isLoading,
    // 計算屬性
    isAuthenticated,
    subscriptionTier,
    renderCredits,
    // 動作
    login,
    register,
    logout,
    refreshToken,
    fetchCurrentUser,
    init,
  }
})
